import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { defaultMST4060_R01DetailDto, defaultMST4060_R01HeaderDto, MST4060_R01DetailDto, MST4060_R01HeaderDto } from './Dto_MST4060_R01';

// 현재 날짜를 조회월 기본 형식인 yyyy-MM 문자열로 반환한다.
const getCurrentYm = () => {
	const date = new Date();
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

export interface MST4060_R01Filter {
	compCd: string;
	equipmentType: string;			// 부품유형
	equipmentCd: string;			// 부품코드
	equipmentNm: string;			// 부품명
	eqmStatus: string;				// 상태
	eqmCd: string;					// 디테일 조회용 부품코드
	searchYm: string;				// 조회 년-월
}

export interface Store_MST4060_R01 {
	MST4060_R01Filter: MST4060_R01Filter;
	MST4060_R01PastFilter: MST4060_R01Filter;
	setMST4060_R01Filter: (filter: Partial<MST4060_R01Filter>) => void;
	setMST4060_R01PastFilter: (filter: Partial<MST4060_R01Filter>) => void;

	MST4060_R01List: MST4060_R01HeaderDto[];
	setMST4060_R01List: (list: MST4060_R01HeaderDto[]) => void;

	selectedMST4060_R01: MST4060_R01HeaderDto | undefined;
	setSelectedMST4060_R01: (data: MST4060_R01HeaderDto | undefined) => void;
	initSelectedMST4060_R01: () => void;

	editMST4060_R01: MST4060_R01HeaderDto;
	setEditMST4060_R01: (data: MST4060_R01HeaderDto) => void;

	MST4060_R01DetailList: MST4060_R01DetailDto[];
	setMST4060_R01DetailList: (data: MST4060_R01DetailDto[]) => void;
	insertMST4060_R01DetailList: (data: MST4060_R01DetailDto[], index?: number) => void;
	removeMST4060_R01DetailList: (index: number) => void;
	updateMST4060_R01DetailList: (index: number, data: MST4060_R01DetailDto) => void;

	selectedMST4060_R01DetailList: string[];
	setSelectedMST4060_R01DetailList: (idList: string[]) => void;
	addSelectedMST4060_R01DetailList: (idList: string[]) => void;
	removeSelectedMST4060_R01DetailList: (idList?: string[]) => void;
	initSelectedMST4060_R01DetailList: () => void;

	deletedMST4060_R01DetailList: string[];
	setDeletedMST4060_R01DetailList: (idList: string[]) => void;
	addDeletedMST4060_R01DetailList: (idList: string[]) => void;
	removeDeletedMST4060_R01DetailList: (idList?: string[]) => void;

	editMST4060_R01Detail: MST4060_R01DetailDto | null;
	setEditMST4060_R01Detail: (editDetail: MST4060_R01DetailDto | null) => void;

	originalMST4060_R01DetailList: MST4060_R01DetailDto[];
	setOriginalMST4060_R01DetailList: (data: MST4060_R01DetailDto[]) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	allCount: number;				setAllCount: (allCount: number) => void;
	detailAllCount: number;			setDetailAllCount: (allCount: number) => void;
	detailFetchTrigger: boolean;	setDetailFetchTrigger: (trigger: boolean) => void;
	baseQty: number;				setBaseQty: (qty: number) => void;

	MST4060_R01GridCalendarState: {
		pickerVisible: boolean;
		pickerPosition: { top: number; left: number };
		targetRowIndex: number | null;
		targetField: string | null;
		targetDate: Date | undefined;
	};
	setMST4060_R01GridCalendarState: (state: Partial<Store_MST4060_R01['MST4060_R01GridCalendarState']>) => void;
	initMST4060_R01GridCalendarState: () => void;

	initData: () => void;
}

// 화면 최초 진입 및 초기화 버튼에서 사용하는 기본 조회조건이다.
export const defaultMST4060_R01Filter: MST4060_R01Filter = {
	compCd: '',
	equipmentType: '',
	equipmentCd: '',
	equipmentNm: '',
	eqmStatus: '',
	eqmCd: '',
	searchYm: getCurrentYm()
}

// 헤더·상세 데이터, 선택/삭제 상태, 그리드 UI 상태를 화면 단위로 관리한다.
export const useMST4060_R01Store = create<Store_MST4060_R01>((set) => ({
	MST4060_R01Filter: defaultMST4060_R01Filter,
	MST4060_R01PastFilter: defaultMST4060_R01Filter,
	// 전달된 값만 현재 조회조건에 병합한다.
	setMST4060_R01Filter: (filter) => set((state) => ({
		MST4060_R01Filter: { ...state.MST4060_R01Filter, ...filter }
	})),
	// 마지막으로 조회한 조건을 별도로 보관한다.
	setMST4060_R01PastFilter: (filter) => set((state) => ({
		MST4060_R01PastFilter: { ...state.MST4060_R01PastFilter, ...filter }
	})),

	MST4060_R01List: [],
	// 조회된 헤더 행에 그리드 식별자와 정렬 인덱스를 보정한다.
	setMST4060_R01List: (list: MST4060_R01HeaderDto[]) => set((state) => {
		let newList: MST4060_R01HeaderDto[] = [];
		let valueIndex = 1;
		list.forEach((item) => {
			newList.push({
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueIndex++,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueIndex - 1,
				orderIndexSeq: Math.max(item.orderIndexSeq, 0)
			});
		});
		return { ...state, MST4060_R01List: newList }
	}),

	selectedMST4060_R01: undefined,
	// 헤더 선택이 바뀌면 이전 상세 선택/삭제 상태를 비우고 상세 재조회를 요청한다.
	setSelectedMST4060_R01: (data: MST4060_R01HeaderDto | undefined) => set((state) => {
		return {
			...state,
			selectedMST4060_R01: data,
			selectedMST4060_R01DetailList: [],
			deletedMST4060_R01DetailList: [],
			detailFetchTrigger: true,
		};
	}),
	// 헤더와 상세 선택 상태를 신규 입력 기준으로 초기화한다.
	initSelectedMST4060_R01: () => set({
		selectedMST4060_R01: undefined,
		editMST4060_R01: { ...defaultMST4060_R01HeaderDto, valueIndex: 0 },
		MST4060_R01DetailList: [],
		detailFetchTrigger: false,
	}),

	editMST4060_R01: defaultMST4060_R01HeaderDto,
	// Info 폼에서 편집할 예비품 헤더를 교체한다.
	setEditMST4060_R01: (data: MST4060_R01HeaderDto) => set((state) => {
		let editMST4060_R01 = defaultMST4060_R01HeaderDto;
		if (data) {
			editMST4060_R01 = { ...data }
		}
		return { ...state, editMST4060_R01: editMST4060_R01 };
	}),

	MST4060_R01DetailList: [],
	// 조회된 상세 행에 중복되지 않는 그리드 식별자와 정렬 인덱스를 부여한다.
	setMST4060_R01DetailList: (data: MST4060_R01DetailDto[]) => set((state) => {
		let newList: MST4060_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item) => {
			newList.push({
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: Math.max(item.orderIndexSeq, 0)
			});
			valueAndOrderIndex++;
		});
		return { ...state, MST4060_R01DetailList: newList }
	}),
	// 신규 상세 행을 지정 위치 또는 목록 끝에 추가한다.
	insertMST4060_R01DetailList: (data: MST4060_R01DetailDto[], index?: number) => set((state) => {
		let newDetailList = [...state.MST4060_R01DetailList];
		let maxValueIndex = Math.max(0, ...newDetailList.map(item => item.valueIndex || 0)) + 1;

		const newData = data.map(item => ({
			...defaultMST4060_R01DetailDto,
			...item,
			valueIndex: maxValueIndex++,
			orderIndex: maxValueIndex - 1,
			orderIndexSeq: 0
		}));

		if (index !== undefined) {
			newDetailList.splice(index, 0, ...newData);
		} else {
			newDetailList.push(...newData);
		}

		return { ...state, MST4060_R01DetailList: newDetailList }
	}),
	// 그리드 식별자에 해당하는 신규 상세 행을 목록에서 제거한다.
	removeMST4060_R01DetailList: (index: number) => set((state) => {
		const newDetailList = [...state.MST4060_R01DetailList].filter((item) => item.valueIndex !== index);
		return { ...state, MST4060_R01DetailList: newDetailList }
	}),
	// 그리드 식별자로 대상 상세 행을 찾아 변경된 데이터로 교체한다.
	updateMST4060_R01DetailList: (valueIndex: number, data: MST4060_R01DetailDto) => set((state) => {
		const _detailList = state.MST4060_R01DetailList.map(item =>
			item.valueIndex === valueIndex ? data : item
		);
		return { ...state, MST4060_R01DetailList: _detailList }
	}),

	selectedMST4060_R01DetailList: [],
	// 상세 그리드에서 현재 선택된 행 식별자 목록을 교체한다.
	setSelectedMST4060_R01DetailList: (idList: string[]) => set((state) => {
		return { ...state, selectedMST4060_R01DetailList: idList }
	}),
	// 기존 상세 선택에 전달된 행 식별자를 추가한다.
	addSelectedMST4060_R01DetailList: (idList: string[]) => set((state) => {
		const _selectedList = [...state.selectedMST4060_R01DetailList];
		if (idList.length > 0) {
			_selectedList.push(...idList);
		}
		return { ...state, selectedMST4060_R01DetailList: _selectedList }
	}),
	// 지정한 행을 상세 선택에서 제외하며, 미지정 시 전체 선택을 해제한다.
	removeSelectedMST4060_R01DetailList: (idList?: string[]) => set((state) => ({
		...state,
		selectedMST4060_R01DetailList: [...state.selectedMST4060_R01DetailList].filter(value => idList ? !idList.includes(value) : false)
	})),
	// 상세 선택과 상세 편집 대상을 기본값으로 초기화한다.
	initSelectedMST4060_R01DetailList: () => set({
		selectedMST4060_R01DetailList: [],
		editMST4060_R01Detail: defaultMST4060_R01DetailDto
	}),

	deletedMST4060_R01DetailList: [],
	// 저장 시 삭제할 상세 행 식별자 목록을 교체한다.
	setDeletedMST4060_R01DetailList: (idList: string[]) => set((state) => {
		return { ...state, deletedMST4060_R01DetailList: idList }
	}),
	// 저장된 행은 삭제 목록에 넣고, 저장 전 신규 행은 목록에서 즉시 제거한다.
	addDeletedMST4060_R01DetailList: (idList: string[]) => set((state) => {
		const _deletedList = [...state.deletedMST4060_R01DetailList];

		for (const valueIndex of idList) {
			const isInserted = state.MST4060_R01DetailList.some(item => item.valueIndex === Number(valueIndex) && item.inserted);
			const isModified = state.MST4060_R01DetailList.some(item => item.valueIndex === Number(valueIndex) && item.isModified);
			if (!isInserted && !isModified) {
				_deletedList.push(valueIndex);
			}
		}

		return { ...state, deletedMST4060_R01DetailList: _deletedList }
	}),
	// 지정한 행을 삭제 대상에서 제외하며, 미지정 시 삭제 대상을 모두 비운다.
	removeDeletedMST4060_R01DetailList: (idList?: string[]) => set((state) => ({
		...state,
		deletedMST4060_R01DetailList: [...state.deletedMST4060_R01DetailList].filter(value => idList ? !idList.includes(value) : false)
	})),

	editMST4060_R01Detail: defaultMST4060_R01DetailDto,
	// 현재 편집 중인 상세 행을 저장한다.
	setEditMST4060_R01Detail: (editDetail: MST4060_R01DetailDto | null) => set((state) => {
		return { ...state, editMST4060_R01Detail: editDetail }
	}),

	originalMST4060_R01DetailList: [],
	// 키 변경과 수정 여부 비교에 사용할 조회 당시의 상세 원본을 보관한다.
	setOriginalMST4060_R01DetailList: (data: MST4060_R01DetailDto[]) => set((state) => {
		let newList: MST4060_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item) => {
			newList.push({
				...defaultMST4060_R01DetailDto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return { ...state, originalMST4060_R01DetailList: newList }
	}),

	columnState: null,
	// 예비품 목록 그리드의 사용자 컬럼 상태를 저장한다.
	setColumnState: (state: ColumnState[] | null) => set({ columnState: state }),
	detailColumnState: null,
	// 입출고 상세 그리드의 사용자 컬럼 상태를 저장한다.
	setDetailColumnState: (state: ColumnState[] | null) => set({ detailColumnState: state }),

	scrollTop: null,
	// 예비품 목록 그리드의 세로 스크롤 위치를 저장한다.
	setScrollTop: (top: number | null) => set({ scrollTop: top }),
	detailScrollTop: null,
	// 입출고 상세 그리드의 세로 스크롤 위치를 저장한다.
	setDetailScrollTop: (top: number | null) => set({ detailScrollTop: top }),

	allCount: 0,
	// 현재 검색조건의 예비품 전체 건수를 저장한다.
	setAllCount: (allCount: number) => set({ allCount }),
	detailAllCount: 0,
	// 선택 예비품과 조회월의 상세 전체 건수를 저장한다.
	setDetailAllCount: (allCount: number) => set({ detailAllCount: allCount }),
	detailFetchTrigger: false,
	// 헤더 선택 후 상세 조회가 필요한지 표시한다.
	setDetailFetchTrigger: (trigger: boolean) => set({ detailFetchTrigger: trigger }),
	baseQty: 0,
	// 조회월 이전 누적 기초재고를 저장한다.
	setBaseQty: (qty: number) => set({ baseQty: qty }),

	MST4060_R01GridCalendarState: {
		pickerVisible: false,
		pickerPosition: { top: 0, left: 0 },
		targetRowIndex: null,
		targetField: null,
		targetDate: undefined
	},
	// 달력의 위치, 대상 행/필드, 표시 여부 중 전달된 값만 갱신한다.
	setMST4060_R01GridCalendarState: (newState: Partial<Store_MST4060_R01['MST4060_R01GridCalendarState']>) => set((state) => ({
		...state,
		MST4060_R01GridCalendarState: { ...state.MST4060_R01GridCalendarState, ...newState }
	})),
	// 상세 그리드 달력 팝업 상태를 닫힌 기본값으로 되돌린다.
	initMST4060_R01GridCalendarState: () => set((state) => ({
		...state,
		MST4060_R01GridCalendarState: {
			pickerVisible: false,
			pickerPosition: { top: 0, left: 0 },
			targetRowIndex: null,
			targetField: null,
			targetDate: undefined
		}
	})),

	// 화면 초기화 시 조회조건과 데이터 및 선택·스크롤·달력 상태를 기본값으로 되돌린다.
	initData: () => set((state) => ({
		...state,
		MST4060_R01Filter: defaultMST4060_R01Filter,
		MST4060_R01PastFilter: { ...defaultMST4060_R01Filter },
		MST4060_R01List: [],
		selectedMST4060_R01: undefined,
		editMST4060_R01: defaultMST4060_R01HeaderDto,
		MST4060_R01DetailList: [],
		deletedMST4060_R01DetailList: [],
		selectedMST4060_R01DetailList: [],
		editMST4060_R01Detail: defaultMST4060_R01DetailDto,
		allCount: 0,
		detailAllCount: 0,
		detailFetchTrigger: false,
		baseQty: 0,
		scrollTop: null,
		detailScrollTop: null,
		MST4060_R01GridCalendarState: {
			pickerVisible: false,
			pickerPosition: { top: 0, left: 0 },
			targetRowIndex: null,
			targetField: null,
			targetDate: undefined
		}
	})),
}));
